/* ============================================================
   computeValue — 계산형/참조형 속성(formula·rollup·person·relation)의
   "표시/비교용 값" 공용 계산 모듈
   - DatabasePage 셀 렌더와 viewPipeline(검색/필터/정렬)이 함께 사용
   - dbx = useRelationContext() 결과 (peopleById / titleById / targets)
   ============================================================ */
import { evaluateFormula, fmtDateVal } from './formulaEngine';
import { rowValue } from './viewPipeline';

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/* rollup 집계 종류 */
export const ROLLUP_AGGS = [
  { id: 'show', label: '원본 표시' },
  { id: 'count', label: '개수' },
  { id: 'sum', label: '합계' },
  { id: 'avg', label: '평균' },
  { id: 'min', label: '최소' },
  { id: 'max', label: '최대' },
  { id: 'checked_pct', label: '체크된 비율' },
];

/* ---------------- relation 값 정규화 ----------------
   저장 포맷(canonical): row page id 문자열 배열 ['<uuid>', ...]
   하위호환: [{ id, title }] 객체 배열(노션 임포트 산출) / 레거시 평문 문자열 */
export function relationIds(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((it) => {
      if (typeof it === 'string') return it;
      if (it && typeof it === 'object') return it.id ?? it.page_id ?? null;
      return null;
    })
    .filter(Boolean);
}

/* relation 값 → 표시용 제목 배열 (객체 title 우선, 없으면 titleById 조회) */
export function relationTitles(value, titleById) {
  if (Array.isArray(value)) {
    return value
      .map((it) => {
        if (it && typeof it === 'object') {
          const id = it.id ?? it.page_id ?? null;
          return it.title ?? it.name ?? titleById?.get?.(id)?.title ?? id ?? '';
        }
        const t = titleById?.get?.(it);
        return t ? (t.title || '제목 없음') : String(it ?? '');
      })
      .filter((s) => s !== '');
  }
  if (value == null || value === '') return [];
  return [String(value)]; // 레거시 평문 ("3개 연결" 등)
}

/* ---------------- person 값 정규화 ----------------
   저장 포맷(canonical): user id 문자열 배열 ['<uuid>', ...]
   하위호환: 레거시 평문 문자열(이름) → 그대로 표시 */
export function personNames(value, peopleById) {
  if (Array.isArray(value)) {
    return value
      .map((v) => {
        const m = peopleById?.get?.(v);
        if (m) return m.display_name || '(이름 없음)';
        return UUID_RE.test(String(v)) ? '(알 수 없음)' : String(v);
      })
      .filter(Boolean);
  }
  if (value == null || value === '') return [];
  return [String(value)];
}

/* ---------------- 표시 포맷 ---------------- */
export function formatVal(v) {
  if (v == null) return '';
  if (v instanceof Date) return fmtDateVal(v);
  if (typeof v === 'boolean') return v ? '참' : '거짓';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return String(v);
    // 부동소수점 잡음 제거 (0.30000000000000004 → 0.3)
    return String(Math.round(v * 1e10) / 1e10);
  }
  return String(v);
}

/* ---------------- formula 계산 ----------------
   반환: { value, error } — 오류 시 value=null, error=한국어 메시지
   depth: formula 가 formula 를 참조할 때 재귀 깊이 (5 초과 = 순환 참조로 간주) */
export function computeFormula(col, row, columns, dbx, depth = 0) {
  const expr = String(col?.formula || '');
  if (!expr.trim()) return { value: '', error: null };
  if (depth > 5) return { value: null, error: '순환 참조 (참조 깊이 5 초과)' };
  try {
    const value = evaluateFormula(expr, (name) => {
      const target = columns.find((c) => c.name === name) || columns.find((c) => c.key === name);
      if (!target) throw new Error(`속성을 찾을 수 없습니다: ${name}`);
      if (target.key === col.key) throw new Error('순환 참조 (자기 자신 참조)');
      if (target.type === 'formula') {
        const r = computeFormula(target, row, columns, dbx, depth + 1);
        if (r.error) throw new Error(r.error);
        return r.value;
      }
      if (target.type === 'rollup') {
        const r = computeRollup(target, row, columns, dbx);
        if (r.error) throw new Error(r.error);
        if (r.pending) return '';
        return r.value ?? '';
      }
      if (target.type === 'person') return personNames(rowValue(row, target), dbx?.peopleById).join(', ');
      if (target.type === 'relation') return relationTitles(rowValue(row, target), dbx?.titleById).join(', ');
      const v = rowValue(row, target);
      if (target.type === 'checkbox') return v === true || v === 'true';
      return v == null ? '' : v;
    });
    return { value, error: null };
  } catch (e) {
    return { value: null, error: e.message || String(e) };
  }
}

/* ---------------- rollup 계산 ----------------
   반환: { value, error, pending }
   - pending=true: 대상 DB 데이터를 아직 불러오는 중 (셀에 '…' 표시)
   - 삭제/접근 불가 대상 행은 집계에서 안전하게 제외 */
export function computeRollup(col, row, columns, dbx) {
  const cfg = col?.rollup || {};
  const relCol = columns.find((c) => c.key === cfg.relationKey && c.type === 'relation');
  if (!relCol) return { value: null, error: '기준 관계(relation) 속성이 없습니다', pending: false };
  const dbId = relCol.relationDb;
  if (!dbId || !UUID_RE.test(String(dbId))) {
    return { value: null, error: '관계 속성에 대상 데이터베이스가 설정되지 않았습니다', pending: false };
  }
  const target = dbx?.targets?.[dbId];
  if (!target || target.loading) return { value: null, error: null, pending: true };
  if (target.error) return { value: null, error: '대상 데이터베이스에 접근할 수 없습니다', pending: false };

  const ids = relationIds(rowValue(row, relCol));
  const relRows = ids.map((id) => target.rowsById[id]).filter(Boolean); // 삭제된 행 skip
  const key = cfg.targetKey || 'title';
  const vals = relRows.map((tr) => (key === 'title' ? tr.title : tr.properties?.[key]));
  const agg = cfg.agg || 'show';

  if (agg === 'count') return { value: relRows.length, error: null, pending: false };
  if (agg === 'show') {
    const text = vals
      .filter((v) => v != null && v !== '')
      .map((v) => (Array.isArray(v) ? relationTitles(v, dbx?.titleById).join(', ') : formatVal(v)))
      .join(', ');
    return { value: text, error: null, pending: false };
  }
  if (agg === 'checked_pct') {
    if (!relRows.length) return { value: '', error: null, pending: false };
    const n = vals.filter((v) => v === true || v === 'true').length;
    return { value: `${Math.round((n / relRows.length) * 100)}%`, error: null, pending: false };
  }
  // 숫자 집계 — 비어 있지 않고 숫자로 해석되는 값만
  const nums = vals
    .filter((v) => v !== '' && v != null && typeof v !== 'boolean')
    .map(Number)
    .filter((n) => !Number.isNaN(n));
  if (agg === 'sum') return { value: nums.reduce((s, n) => s + n, 0), error: null, pending: false };
  if (!nums.length) return { value: '', error: null, pending: false };
  if (agg === 'avg') return { value: Math.round((nums.reduce((s, n) => s + n, 0) / nums.length) * 100) / 100, error: null, pending: false };
  if (agg === 'min') return { value: Math.min(...nums), error: null, pending: false };
  if (agg === 'max') return { value: Math.max(...nums), error: null, pending: false };
  return { value: '', error: null, pending: false };
}

/* ---------------- 파이프라인용 값 함수 ----------------
   applyPipeline 의 getValue 로 주입: 검색/필터/정렬이
   formula/rollup 계산값, person 이름, relation 제목 기준으로 동작하게 한다. */
export function makeGetValue(columns, dbx) {
  return (row, col) => {
    if (!col) return '';
    if (col.type === 'formula') {
      const r = computeFormula(col, row, columns, dbx);
      if (r.error) return '#오류';
      return typeof r.value === 'number' ? r.value : formatVal(r.value);
    }
    if (col.type === 'rollup') {
      const r = computeRollup(col, row, columns, dbx);
      if (r.pending) return '';
      if (r.error) return '#오류';
      return typeof r.value === 'number' ? r.value : (r.value ?? '');
    }
    if (col.type === 'person') return personNames(rowValue(row, col), dbx?.peopleById).join(', ');
    if (col.type === 'relation') return relationTitles(rowValue(row, col), dbx?.titleById).join(', ');
    return rowValue(row, col);
  };
}
